import styled from "@emotion/styled";
import { SPRING_ANIMATION } from "config/consts";
import { motion } from "framer-motion";
import { useDeviceBasedTextStyles } from "hooks/useDeviceBasedTextStyles";
import {
  DeviceType,
  useDeviceType,
  useIsDesktop,
  useIsMobile,
  useIsTablet,
} from "hooks/useDeviceType";
import { debounce } from "lodash-es";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { MARKER_SHIFT_UP_HEIGHT } from "./Ruler";

// The % label on top of the ruler on the Percentage Slider
export const RulerPip = ({
  inputRef,
  isInputFocused,
  setIsInputFocused,
  value,
  setValue,
  color,
  isRulerMoving,
  positionScroll,
}: {
  inputRef: RefObject<HTMLInputElement>;
  isInputFocused: boolean;
  setIsInputFocused: Dispatch<SetStateAction<boolean>>;
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number>> | ((value: number) => void);
  color: string;
  isRulerMoving: boolean;
  positionScroll: (scrollToValue?: number) => void;
}) => {
  const [draftInputValue, setDraftInputValue] = useState<number | null>(null);
  const textEstimatorRef = useRef<HTMLDivElement>(null);
  const textStyles = useDeviceBasedTextStyles({ shouldScale: true });
  const theme = GlobalStore.useState(s => s.theme);
  const getWidthOfInput = useCallback(
    (inputValue: number) => {
      const tempElement = document.createElement("div");
      tempElement.style.font = textStyles["Title"]["XL"];
      tempElement.innerHTML = String(inputValue);
      textEstimatorRef.current?.appendChild(tempElement);
      const width = tempElement.getBoundingClientRect().width;
      textEstimatorRef.current?.removeChild(tempElement);
      return width;
    },
    [textStyles]
  );
  const [inputWidth, setInputWidth] = useState(getWidthOfInput(value));
  const [isFirstRender, setIsFirstRender] = useState(true);
  const deviceType = useDeviceType();

  const baseWidth = useMemo(() => {
    if (deviceType === DeviceType.Desktop) return 48;
    if (deviceType === DeviceType.Tablet) return 40;
    return 36;
  }, [deviceType]);

  const updateInputWidth = useCallback(
    (inputValue: number) => {
      setInputWidth(getWidthOfInput(inputValue) + baseWidth);
    },
    [baseWidth, getWidthOfInput]
  );

  const valueToDisplay = useMemo(() => {
    if (isInputFocused) {
      if (draftInputValue === null) return "";
      return Number(draftInputValue);
    } else {
      return Number(value);
    }
  }, [isInputFocused, draftInputValue, value]);

  const updateWidthByFont = useCallback(() => {
    setTimeout(() => {
      if (!document.fonts.check(`14px ${theme.fontFamilies.Primary}`)) {
        updateWidthByFont();
        return;
      }
      updateInputWidth(value);
    }, 300);
  }, [theme.fontFamilies.Primary, updateInputWidth, value]);

  useEffect(() => {
    // First render might use the width of the system font instead of Work Sans,
    // therefore this prevents the input from prematurely cutting its contents
    // off if the original font is shorter in width
    if (isFirstRender && !document.fonts.check(`14px ${theme.fontFamilies.Primary}`)) {
      updateWidthByFont();
      setIsFirstRender(false);
    } else if (!isInputFocused || draftInputValue === null) {
      updateInputWidth(value);
    }
  }, [
    draftInputValue,
    isFirstRender,
    isInputFocused,
    theme.fontFamilies.Primary,
    updateInputWidth,
    updateWidthByFont,
    value,
  ]);

  const debouncedSetValue = useCallback(
    (newValue: number) => {
      positionScroll(newValue);
      setValue(Number(newValue));
    },
    [positionScroll, setValue]
  );

  const delayedSetValue = useMemo(
    () => debounce(debouncedSetValue, 300, { trailing: true }),
    [debouncedSetValue]
  );

  return (
    <RulerPipContainer
      color={color}
      ref={textEstimatorRef}
      animate={{ y: isRulerMoving || isInputFocused ? -MARKER_SHIFT_UP_HEIGHT : 0 }}
      transition={SPRING_ANIMATION}
    >
      <RulerPipInput
        ref={inputRef}
        value={valueToDisplay}
        type="number"
        color={color}
        width={inputWidth}
        onKeyDown={e => {
          if (e.key === "Enter") {
            inputRef.current?.blur();
          }
        }}
        onFocus={() => {
          setIsInputFocused(true);
          setDraftInputValue(null);
        }}
        onKeyDownCapture={e => {
          if (e.key === ".") e.preventDefault();
        }}
        onChange={e => {
          if (e.target.value.endsWith(".")) return;
          const newValue = Number(e.target.value);
          if (newValue > 100 || newValue < 0) return;
          // Update input width dynamically
          setDraftInputValue(newValue === 0 ? null : newValue);
          if (newValue === 0) {
            delayedSetValue(value);
            return;
          }
          updateInputWidth(newValue);
          delayedSetValue(newValue);
        }}
        onBlur={() => {
          setIsInputFocused(false);
          if (draftInputValue == null) return;
          setValue(draftInputValue);
        }}
      />
      {isInputFocused && draftInputValue === null && (
        <RulerPipPlaceholder width={inputWidth}>{value}</RulerPipPlaceholder>
      )}
      <RulerPipInputSuffix>%</RulerPipInputSuffix>
    </RulerPipContainer>
  );
};

// Sub-components

const RulerPipContainer = styled(motion.div)(() => {
  return {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    top: 6,
  };
});

const RulerPipInput = styled.input<{ color: string; width: number }>(props => {
  const textStyles = useDeviceBasedTextStyles({ shouldScale: true });
  const isDesktop = useIsDesktop();
  const isTablet = useIsTablet();
  const padding = useMemo(() => {
    if (isDesktop) return "6px 35px 6px 10px";
    if (isTablet) return "6px 30px 6px 10px";
    return "6px 28px 6px 8px";
  }, [isDesktop, isTablet]);

  return {
    borderRadius: 12,
    padding,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: props.width,
    textAlign: "right",
    font: textStyles["Title"]["XL"],
    color: props.theme.button.secondary.text.default,
    background: props.color,
    border: "none",
    zIndex: 1000,
    ":focus": {
      border: "none",
      outline: "none",
    },
  };
});

const RulerPipPlaceholder = styled.div<{ width: number }>(props => {
  const textStyles = useDeviceBasedTextStyles({ shouldScale: true });
  const isDesktop = useIsDesktop();
  const isTablet = useIsTablet();
  const padding = useMemo(() => {
    if (isDesktop) return "6px 35px 6px 10px";
    if (isTablet) return "6px 30px 6px 10px";
    return "6px 28px 6px 8px";
  }, [isDesktop, isTablet]);

  return {
    position: "absolute",
    borderRadius: 12,
    padding,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: props.width,
    textAlign: "right",
    font: textStyles["Title"]["XL"],
    color: props.theme.button.secondary.text.default,
    opacity: 0.5,
    background: props.color,
    border: "none",
    zIndex: 1010,
    ":focus": {
      border: "none",
      outline: "none",
    },
  };
});

const RulerPipInputSuffix = styled.span(props => {
  const textStyles = useDeviceBasedTextStyles({ shouldScale: true });
  const isMobile = useIsMobile();
  return {
    font: textStyles["Title"]["XL"],
    color: props.theme.button.secondary.text.default,
    position: "absolute",
    top: 6,
    right: isMobile ? 8 : 10,
    zIndex: 1010,
  };
});
